/**
 * Advertiser tracking_ID generator
 *
 * Formula: tracking_ID = hashToCurve(scope) · nymSecret
 *   where scope = lengthPrefixedConcat(DST, campaign_ref, rotation_nonce)
 *
 * Length-prefixing prevents encoding collisions where different
 * (campaign_ref, rotation_nonce) splits would produce the same scope bytes.
 *
 * @traces R4.2, R10.2
 */
import { lengthPrefixedConcat, utf8 } from '../crypto/encoding.js';
import { normalizeCanonicalDomain } from './canonical-domain.js';
const TRACKING_ID_DST = utf8('OwnYou-TrackingID-v1');
const CAMPAIGN_REF_DST = utf8('OwnYou-CampaignRef-v1');
/**
 * Build the hash-to-curve scope bytes for a tracking_ID. Length-prefixed so
 * the encoding is injective: no two distinct (campaign_ref, nonce) pairs
 * produce the same bytes.
 */
export function trackingIdScope(campaignRef, rotationNonce) {
    if (!(campaignRef instanceof Uint8Array)) {
        throw new TypeError('trackingIdScope: campaignRef must be Uint8Array');
    }
    if (!(rotationNonce instanceof Uint8Array)) {
        throw new TypeError('trackingIdScope: rotationNonce must be Uint8Array');
    }
    return lengthPrefixedConcat([TRACKING_ID_DST, campaignRef, rotationNonce]);
}
/**
 * Generate an advertiser tracking_ID.
 *
 * @param suite - CryptoSuite implementation
 * @param nymSecret - 32-byte nymSecret
 * @param campaignRef - Campaign reference bytes (from computeCampaignRef)
 * @param rotationNonce - Rotation epoch nonce
 * @returns 48-byte compressed G1 point
 *
 * @traces R4.2, R10.2
 */
export function generateTrackingID(suite, nymSecret, campaignRef, rotationNonce) {
    if (!(nymSecret instanceof Uint8Array) || nymSecret.length !== 32) {
        throw new TypeError('generateTrackingID: nymSecret must be 32-byte Uint8Array');
    }
    const scope = trackingIdScope(campaignRef, rotationNonce);
    const basePoint = suite.hashToCurve(scope);
    return suite.scalarMultiply(basePoint, nymSecret);
}
/**
 * Compute `campaign_ref` from canonical_domain and campaign_ID.
 *
 *     campaign_ref = hash(lengthPrefixedConcat(DST, normalized_domain, campaign_ID))
 *
 * The domain is normalized per R10.2.1 (lowercase, strip scheme/path/trailing
 * punctuation, www-exception only). Length-prefixing prevents split ambiguity
 * (e.g. `{"a.com:", "b"}` vs `{"a.com", ":b"}`).
 *
 * @traces R10.2, R10.2.1
 */
export function computeCampaignRef(suite, canonicalDomain, campaignId) {
    if (typeof canonicalDomain !== 'string') {
        throw new TypeError('computeCampaignRef: canonicalDomain must be string');
    }
    if (typeof campaignId !== 'string') {
        throw new TypeError('computeCampaignRef: campaignId must be string');
    }
    const normalized = normalizeCanonicalDomain(canonicalDomain);
    const input = lengthPrefixedConcat([
        CAMPAIGN_REF_DST,
        utf8(normalized),
        utf8(campaignId),
    ]);
    return suite.hash(input);
}
